import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Users, Star, Clock, GraduationCap, CheckCircle, CheckCircle2, MessageSquare, Calendar, Award, BookOpen, Search, Filter, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TutorCollaboration = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [formData, setFormData] = useState({
    studentName: '',
    parentName: '',
    email: '',
    phone: '',
    subject: '',
    level: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadTutors();
  }, []);

  const loadTutors = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/tutors`);
      setTutors(response.data.tutors || []);
    } catch (error) {
      console.error('Error loading tutors:', error);
      // Show placeholder tutors if no approved tutors yet
      setTutors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitInquiry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/tutor/inquiry`, {
        ...formData,
        tutorId: selectedTutor?.id,
        tutorName: selectedTutor?.name
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Inquiry sent successfully! The tutor will contact you soon.');
      setShowInquiryForm(false);
      setFormData({
        studentName: '',
        parentName: '',
        email: '',
        phone: '',
        subject: '',
        level: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Get all unique subjects from tutors
  const allSubjects = [...new Set(tutors.flatMap(t => t.subjects || []))];

  // Filter tutors based on search and subject filter
  const filteredTutors = tutors.filter(tutor => {
    const matchesSearch = tutor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          tutor.bio?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSubject = subjectFilter === 'all' || (tutor.subjects || []).includes(subjectFilter);
    return matchesSearch && matchesSubject;
  });

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm shadow-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-white/20">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Users className="h-6 w-6 text-white" />
              <h1 className="text-2xl font-bold text-white">Tutor Marketplace</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Find Your Perfect Tutor
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Connect with verified, experienced tutors who can help your child excel in their exams. 
            All tutors are vetted and approved by our team.
          </p>
        </div>

        {/* Search and Filter */}
        <Card className="mb-8 shadow-xl">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search tutors by name or expertise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subjects</SelectItem>
                  {allSubjects.map(subject => (
                    <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
            <span className="ml-3 text-white text-lg">Loading tutors...</span>
          </div>
        )}

        {/* No Tutors State */}
        {!loading && filteredTutors.length === 0 && (
          <Card className="shadow-xl">
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-700 mb-2">No Tutors Available Yet</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                We're currently building our network of verified tutors. 
                Check back soon or contact us for personalized tutor recommendations.
              </p>
              <Button onClick={() => navigate('/contact')} className="bg-purple-600 hover:bg-purple-700">
                Contact Us for Help
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Tutors Grid */}
        {!loading && filteredTutors.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTutors.map((tutor) => (
              <Card key={tutor.id} className="shadow-xl hover:shadow-2xl transition-all overflow-hidden group">
                <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white pb-12 relative">
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
                    {tutor.profile_image ? (
                      <img 
                        src={tutor.profile_image} 
                        alt={tutor.name}
                        className="w-20 h-20 rounded-full border-4 border-white shadow-lg object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full border-4 border-white shadow-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-2xl font-bold text-white">
                        {getInitials(tutor.name)}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-12 pb-6 px-6">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">{tutor.name}</h3>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      <span className="font-semibold">{tutor.rating || 4.5}</span>
                      <span className="text-gray-500 text-sm">({tutor.reviews_count || 0} reviews)</span>
                    </div>
                  </div>

                  {/* Subjects */}
                  <div className="flex flex-wrap justify-center gap-2 mb-4">
                    {(tutor.subjects || []).slice(0, 3).map((subject, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-purple-100 text-purple-700">
                        {subject}
                      </Badge>
                    ))}
                    {(tutor.subjects || []).length > 3 && (
                      <Badge variant="outline">+{tutor.subjects.length - 3}</Badge>
                    )}
                  </div>

                  {/* Bio */}
                  <p className="text-gray-600 text-sm text-center mb-4 line-clamp-3">
                    {tutor.bio || 'Experienced tutor ready to help your child succeed.'}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="h-4 w-4 text-purple-500" />
                      <span>{tutor.experience_years || 0}+ years exp</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <GraduationCap className="h-4 w-4 text-purple-500" />
                      <span>£{tutor.hourly_rate || 40}/hour</span>
                    </div>
                  </div>

                  {/* Availability */}
                  {tutor.availability && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4 justify-center">
                      <Calendar className="h-4 w-4 text-green-500" />
                      <span>{tutor.availability}</span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        setSelectedTutor(tutor);
                        setShowInquiryForm(true);
                      }}
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Contact
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Section */}
        <Card className="mt-12 shadow-xl bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Verified Tutors</h3>
                <p className="text-gray-600 text-sm">All tutors are vetted and approved by our team</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Qualified Experts</h3>
                <p className="text-gray-600 text-sm">Experienced educators with proven track records</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Personalized Learning</h3>
                <p className="text-gray-600 text-sm">Tailored sessions to meet your child's needs</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Are You a Tutor Section */}
        <Card className="mt-8 shadow-xl border-2 border-green-400 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 text-center">
            <h2 className="text-3xl font-bold mb-2">Teach. Inspire. Earn.</h2>
            <p className="text-white/90 text-lg">Join Edpies as a Tutor or Educator</p>
          </div>
          <CardContent className="p-8">
            <p className="text-gray-700 text-center mb-6 max-w-2xl mx-auto">
              Are you a tutor or educator ready to make a real difference? Collaborate with Edpies to empower community kids, 
              transform learning experiences, and unlock a flexible side-earning opportunity.
            </p>
            
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <p className="text-gray-700 mb-4">
                At Edpies, we believe great educators change lives. We're inviting passionate tutors and educators 
                to collaborate with us in shaping brighter futures for community children.
              </p>
              <p className="font-semibold text-gray-800 mb-3">By joining Edpies, you'll have the opportunity to:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Make a real, measurable impact on students' learning journeys</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Be part of a growing, purpose-driven education community</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Teach flexibly, on your own terms</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Earn additional income while doing what you love</span>
                </li>
              </ul>
            </div>
            
            <p className="text-gray-600 text-center mb-6 italic">
              Whether you're an experienced tutor or an educator looking to extend your reach, 
              Edpies gives you the platform, tools, and support to teach smarter and connect deeper with students.
            </p>
            
            <p className="text-center font-semibold text-purple-700 mb-6">
              Together, let's transform learning—one child at a time.
            </p>
            
            <div className="text-center">
              <Button 
                onClick={() => navigate('/auth')} 
                size="lg"
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-lg px-8"
              >
                Register as a Tutor
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inquiry Modal */}
      {showInquiryForm && selectedTutor && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Contact {selectedTutor.name}</CardTitle>
              <CardDescription>Send an inquiry to arrange tutoring sessions</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitInquiry} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Student Name</Label>
                    <Input
                      name="studentName"
                      value={formData.studentName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Parent/Guardian Name</Label>
                    <Input
                      name="parentName"
                      value={formData.parentName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Select 
                      value={formData.subject} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {(selectedTutor.subjects || ['Maths', 'English']).map(subject => (
                          <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Level</Label>
                    <Select 
                      value={formData.level} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, level: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7+">7+</SelectItem>
                        <SelectItem value="11+">11+</SelectItem>
                        <SelectItem value="GCSE">GCSE</SelectItem>
                        <SelectItem value="A-Level">A-Level</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your tutoring needs..."
                    rows={4}
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowInquiryForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Inquiry'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TutorCollaboration;
